import React, { useCallback, useEffect, useRef } from 'react';

import { jwtDecode } from 'jwt-decode';
import { useHistory, useLocation } from 'react-router-dom';
import UserActions from '~/stormpath/actions/UserActions';

import { Box, Button } from '@mui/material';

import { GoogleTokenData } from '~/types/google';

function SocialGoogleButton() {
  const history = useHistory();
  const location = useLocation();
  const buttonRef = useRef<HTMLDivElement>(null);

  // For review/canary apps, redirect to shared auth domain for Google OAuth
  const handleSharedAuthRedirect = useCallback(() => {
    const returnTo = encodeURIComponent(window.location.origin);
    window.location.href = `https://${window.configs.GOOGLE_AUTH_REDIRECT_DOMAIN}/google/auth/init?return_to=${returnTo}`;
  }, []);

  useEffect(() => {
    // Skip Google button rendering for review/canary apps — they use shared auth domain redirect
    if (window.configs.GOOGLE_AUTH_REDIRECT_DOMAIN) {
      return;
    }

    /* global google */
    (google as any).accounts.id.initialize({
      client_id: window.configs.GOOGLE_APP_ID,
      callback: onSuccess,
    });

    if (buttonRef.current) {
      (google as any).accounts.id.renderButton(buttonRef.current, {
        theme: 'filled_blue',
        text: 'continue_with',
        locale: 'en_AU',
        size: 'large',
      });
    }
  }, []);

  function onSuccess(data) {
    const tokenData = jwtDecode<GoogleTokenData>(data.credential);
    const loginWithGoogleData = {
      id_google: tokenData.sub,
      email: tokenData.email,
      token_id: data.credential,
      givenName: tokenData.given_name,
      surname: tokenData.family_name,
    };
    UserActions.loginWithGoogle(loginWithGoogleData, (err, result) => {
      let redirectUrl = '/';
      if (location) {
        redirectUrl = location.pathname + location.search;
      }
      history.push(redirectUrl);
    });
  }

  // For review/canary apps, render a branded Google button that redirects to shared auth domain
  // Styled to match Google's native filled_blue button: https://developers.google.com/identity/branding-guidelines
  // Uses the same "G" logo and dimensions as google.accounts.id.renderButton
  if (window.configs.GOOGLE_AUTH_REDIRECT_DOMAIN) {
    return (
      <Button
        variant="contained"
        onClick={handleSharedAuthRedirect}
        startIcon={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fff',
              borderRadius: '3px 0 0 3px', // Left-only radius to match GSI: rounded on button edge, flat where it meets blue area
              width: 36, // 36px icon in 40px button = 2px gap top/bottom, matching GSI internal structure
              height: 36,
            }}
          >
            <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google" width={20} height={20} />
          </Box>
        }
        sx={{
          textTransform: 'none',
          fontFamily: '"Google Sans", Roboto, Arial, sans-serif',
          fontWeight: 500,
          fontSize: '14px',
          letterSpacing: '0.25px',
          color: '#fff',
          backgroundColor: '#1a73e8',
          borderRadius: '4px',
          width: 229, // Match native GSI iframe width
          height: 40, // GSI iframe is 44px with margin: -2px, so internal button is 40px
          justifyContent: 'flex-start', // Left-align icon flush with button edge like native GSI
          pl: '2px', // Small gap between button edge and white icon square
          pr: 1.5,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#1765cc',
            boxShadow: '0 1px 2px 0 rgba(66,133,244,.3), 0 1px 3px 1px rgba(66,133,244,.15)',
          },
          '& .MuiButton-startIcon': {
            marginRight: '10px', // 10px gap per Google branding guidelines for web
            marginLeft: 0,
          },
        }}
      >
        Continue with Google
      </Button>
    );
  }

  return (
    <div ref={buttonRef} id="my-signin2">
      Continue with Google
    </div>
  );
}

export default SocialGoogleButton;
